import React, { Fragment, memo } from 'react'
import { Radio, RadioGroup } from 'react-radio-group'

import { useUnit } from 'effector-react'

import { useCallback } from 'hooks'

import { urlTo } from 'helpers/router'

import { SippTransferRequestAssetType } from 'app/effector/nominees-transfers/request/sipp'

import Link from 'components/_old/Link/Link.jsx'
import { Typo } from 'components/_old/Typo'

import { Paper } from 'components/atoms/Paper'
import { Stack } from 'components/atoms/Stack'
import { Typography } from 'components/atoms/Typography'
import { Validate } from 'components/atoms/Validate'

import { RadioWithLabel } from 'components/molecules/WithLabel/WithLabel.jsx'

import { $form, $validation, setField } from '../../index'

const AssetTransferType = memo(() => {
  const [form, validation] = useUnit([$form, $validation])

  const handleChange = useCallback((value: string | null, onAfterChange?: () => void) => {
    setField({
      field: 'asset_transfer_type',
      value,
    })

    if (onAfterChange) {
      onAfterChange()
    }
  }, [])

  return (
    <Validate rules={validation.asset_transfer_type.rules}>
      {(isValid, brokenRule, onFocus, onBlur) => (
        <Fragment>
          <Paper bottom={16}>
            <Typography weight="semibold">
              <Typo>Transfer options</Typo>
            </Typography>
          </Paper>
          <RadioGroup
            className={isValid ? undefined : 'Input_error'}
            selectedValue={form.asset_transfer_type ?? ''}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={(value: string) => {
              handleChange(value, onBlur)
            }}
          >
            <Stack vertical={4}>
              <RadioWithLabel
                CustomInput={Radio}
                label={
                  <Paper top={2} left={4}>
                    <Typography size={14} weight="semibold" lineHeight="small">
                      <Typo>Transfer your existing ETFs (in-specie)</Typo>
                    </Typography>
                    <Paper top={8}>
                      <Typography size={14}>
                        <Typo>
                          Move your existing ETFs to InvestEngine without selling them (known as an in-specie transfer).
                          Other investments will be sold and transferred as cash. See our{' '}
                          <Link to={urlTo('universe-all')} blank hard>
                            ETF range
                          </Link>{' '}
                          to check which ETFs we accept.
                        </Typo>
                      </Typography>
                    </Paper>
                  </Paper>
                }
                value={SippTransferRequestAssetType.IN_SPECIE}
                valid={isValid}
                size="smaller"
                testId="in-specie-transfer"
                name="asset_transfer_type"
                data-test-id="in-specie-transfer"
              />
              <RadioWithLabel
                CustomInput={Radio}
                label={
                  <Paper top={2} left={4}>
                    <Typography size={14} weight="semibold" lineHeight="small">
                      <Typo>Transfer your pension as cash</Typo>
                    </Typography>
                    <Paper top={8}>
                      <Typography size={14}>
                        <Typo>
                          Your current investments will be sold and transferred to InvestEngine as cash for you to
                          invest.
                        </Typo>
                      </Typography>
                    </Paper>
                  </Paper>
                }
                value={SippTransferRequestAssetType.ENCASH}
                valid={isValid}
                size="smaller"
                testId="cash-transfer"
                name="asset_transfer_type"
                data-test-id="cash-transfer"
              />
            </Stack>
          </RadioGroup>
          {!isValid && (
            <Paper top={8} left={32}>
              <Typography color="error" size={14}>
                <Typo>{validation.asset_transfer_type.errors[brokenRule]}</Typo>
              </Typography>
            </Paper>
          )}
        </Fragment>
      )}
    </Validate>
  )
})
AssetTransferType.displayName = 'AssetTransferType'

export { AssetTransferType }
